import { useEffect, useMemo, useState } from "react";
import {
  aggregateContributionsByAquifer,
  aggregateContributionsByOutlet,
  aggregateContributionsByZone,
} from "../engine/aggregation.ts";
import type { SubBasinResult } from "../engine/engine.ts";
import { useT, translateError } from "../i18n/hooks.ts";
import type { TextKey } from "../i18n/es.ts";
import type { CalculationOptions } from "../model/calculationOptions.ts";
import {
  dateRangeIndices,
  trimSeriesByIndexRange,
  type DailySeries,
  type ProjectSeries,
} from "../model/dailySeries.ts";
import { useSubBasinDetail } from "./useCalculation.ts";
import {
  downloadText,
  generateSubBasinContributionsCsv,
  generateAggregatedContributionsCsv,
} from "../engine/exportCsv.ts";
import {
  nativeGranularity,
  GRANULARITY_LABEL_KEY,
  GRANULARITY_PERIOD_KEY,
  type Granularity,
} from "../model/aggregates.ts";
import type { EvalHidProject } from "../model/types.ts";
import {
  activeUnitsCoefficient,
  chooseUnitsHa,
  chooseUnitsKm2,
  chooseCustomUnits,
  activeUnitName,
  UNITS_HA,
  UNITS_KM2,
  UNITS_OTHER,
} from "../model/units.ts";
import { ContributionChart } from "./ContributionChart.tsx";
import { AggregatedContributionChart } from "./AggregatedContributionChart.tsx";
import { DetailChart } from "./DetailChart.tsx";
import { DownloadIcon } from "./DownloadIcon.tsx";
import { InfoIcon } from "./InfoIcon.tsx";
import type { ResultLevel, ResultSelection } from "./resultSelection.ts";
import {
  buildAggregatedRows,
  buildAquiferRows,
  buildSubBasinRows,
  periodDivisor,
  type AggregatedRow,
  type AquiferRow,
} from "./resultRows.ts";

/**
 * Units selector (area×runoff → volume) — web equivalent of the original's
 * "Tools" tab (Tools.vb/APO_UNIDADES, see units.ts). Unlike other options
 * from the original, this one DOES change the real numbers: it applies on
 * the fly, without recalculating (the coefficient only enters the
 * aggregation, not the engine itself).
 */
function UnitsSelector({
  project,
  onChangeProject,
}: {
  project: EvalHidProject;
  onChangeProject: (project: EvalHidProject) => void;
}) {
  const active = project.unitOptions.find((o) => o.checked) ?? project.unitOptions[0];
  const otherRow = project.unitOptions.find((o) => o.code === UNITS_OTHER);
  // Only seed from the project's own row once it's genuinely been
  // customized (value > 0) — otherwise `otherRow.units` is still the raw
  // seed's "Otras" (see DEFAULT_UNIT_OPTIONS, units.ts), which isn't a real
  // name the user chose, just the Spanish label the original desktop
  // template stores there. Same guard `otherValue` already used below it.
  const [otherName, setOtherName] = useState(otherRow && otherRow.value > 0 ? otherRow.units : "");
  const [otherValue, setOtherValue] = useState(
    otherRow && otherRow.value > 0 ? String(otherRow.value) : ""
  );
  const [error, setError] = useState<string | null>(null);
  // Which radio is checked — kept separate from `active` (the project's
  // actually-applied option) so that clicking "Other" doesn't leave the
  // previous choice (Km²/ha) looking checked too: those two apply
  // immediately on click, but "Other" only reveals its fields and needs
  // "Apply" pressed, so `active` doesn't move to it right away.
  const [selectedCode, setSelectedCode] = useState(active.code);
  const showOtherFields = selectedCode === UNITS_OTHER;
  const t = useT();

  function applyCustom() {
    try {
      onChangeProject(chooseCustomUnits(project, otherName, Number(otherValue)));
      setError(null);
    } catch (err) {
      setError(translateError(err, t));
    }
  }

  return (
    <div className="assigner-controls units-selector">
      <label>{t("units.title")}</label>
      <div className="units-selector-options">
        <label>
          <input
            type="radio"
            name="units"
            checked={selectedCode === UNITS_KM2}
            onChange={() => {
              setSelectedCode(UNITS_KM2);
              onChangeProject(chooseUnitsKm2(project));
            }}
          />{" "}
          {t("units.km2")}
        </label>
        <label>
          <input
            type="radio"
            name="units"
            checked={selectedCode === UNITS_HA}
            onChange={() => {
              setSelectedCode(UNITS_HA);
              onChangeProject(chooseUnitsHa(project));
            }}
          />{" "}
          {t("units.ha")}
        </label>
        <label>
          <input
            type="radio"
            name="units"
            checked={selectedCode === UNITS_OTHER}
            onChange={() => setSelectedCode(UNITS_OTHER)}
          />{" "}
          {t("units.other")}
        </label>
      </div>
      {showOtherFields && (
        <div className="units-selector-other">
          <input
            type="text"
            placeholder={t("units.namePlaceholder")}
            value={otherName}
            onChange={(e) => setOtherName(e.target.value)}
          />
          <input
            placeholder={t("units.coefficientPlaceholder")}
            type="number"
            step="any"
            value={otherValue}
            onChange={(e) => setOtherValue(e.target.value)}
          />
          <button className="button" onClick={applyCustom}>
            {t("common.apply")}
          </button>
        </div>
      )}
      {error && <p className="error">{error}</p>}
    </div>
  );
}

/** Table for one aggregation (outlet point or zone) — same 4 columns, same selection behavior for both. */
function AggregatedTable({
  columnLabel,
  unit,
  granularity,
  rows,
  selectedId,
  onSelectRow,
}: {
  columnLabel: string;
  unit: string;
  granularity: Granularity;
  rows: AggregatedRow[];
  selectedId: number | null;
  onSelectRow: (id: number) => void;
}) {
  const t = useT();
  return (
    <div className="table-scroll">
      <table>
        <thead>
          <tr>
            <th>{columnLabel}</th>
            <th>
              {t("resultsPanel.totalPerPeriod", {
                unit,
                period: t(GRANULARITY_PERIOD_KEY[granularity]),
              })}
            </th>
            <th>{t("resultsPanel.surface")}</th>
            <th>{t("resultsPanel.groundwater")}</th>
            <th>{t("resultsPanel.noBreakdown")}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((f) => (
            <tr
              key={f.id}
              className={f.id === selectedId ? "row-selected" : undefined}
              onClick={() => onSelectRow(f.id)}
            >
              <td>{f.name}</td>
              <td>{f.hm3PerPeriod.toFixed(2)}</td>
              <td>{f.hm3SurfacePerPeriod.toFixed(2)}</td>
              <td>{f.hm3GroundwaterPerPeriod.toFixed(2)}</td>
              <td>{f.hm3UnbrokenDownPerPeriod.toFixed(2)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Table for the aquifer level — different shape from AggregatedTable's (no sup/subt breakdown, see AquiferRow's own comment). */
function AquiferTable({
  unit,
  granularity,
  rows,
  selectedId,
  onSelectRow,
}: {
  unit: string;
  granularity: Granularity;
  rows: AquiferRow[];
  selectedId: number | null;
  onSelectRow: (id: number) => void;
}) {
  const t = useT();
  return (
    <div className="table-scroll">
      <table>
        <thead>
          <tr>
            <th>{t("legend.aquifer")}</th>
            <th>
              {t("resultsPanel.totalPerPeriod", {
                unit,
                period: t(GRANULARITY_PERIOD_KEY[granularity]),
              })}
            </th>
            <th>{t("resultsPanel.aquiferRecharge")}</th>
            <th>{t("resultsPanel.aquiferPumping")}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((f) => (
            <tr
              key={f.id}
              className={f.id === selectedId ? "row-selected" : undefined}
              onClick={() => onSelectRow(f.id)}
            >
              <td>{f.name}</td>
              <td>{f.hm3DischargePerPeriod.toFixed(2)}</td>
              <td>{f.hm3InfiltrationPerPeriod.toFixed(2)}</td>
              <td>{f.hm3PumpingPerPeriod.toFixed(2)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const LEVELS: { id: ResultLevel; label: TextKey }[] = [
  { id: "outletPoint", label: "legend.outletPoint" },
  { id: "zone", label: "resultsPanel.zone" },
  { id: "subBasin", label: "resultsPanel.subBasin" },
  { id: "aquifer", label: "legend.aquifer" },
];

/**
 * Body of the calculation-engine results panel (real/generic hybrid — see
 * src/engine/engine.ts). Four aggregation levels, in tabs: outlet point
 * (the real result, see aggregation.ts), zone (this web's own view),
 * sub-basin (diagnostic, unaggregated) and aquifer (each aquifer's own
 * Vo/Alfa balance — `aggregateContributionsByAquifer`, aggregation.ts).
 *
 * ⚠️ Previously permanently disabled: an earlier analysis, based on the
 * decompiled Evalhid.dll, concluded the original .exe never really
 * calculated aquifer infiltration at all (`Ac.acumula_acu` seemed to sum a
 * field, `Cuencas.inf`, that no real model ever assigned). With the real
 * (non-decompiled) C# source this turned out to be wrong — the aquifer's
 * own balance genuinely recedes with its own Vo/Alfa, fed by the aggregated
 * underground component of its linked sub-basins (2026-08, see CLAUDE.md's
 * engine-source audit).
 *
 * The selection (`selection`/`onSelect`) is shared with the map
 * (ModoResultadosMapa in SubBasinMap.tsx): clicking a sub-basin, an outlet
 * point or an aquifer there selects the same thing here, and switches the
 * active tab if needed. The zone has no geometry of its own — it can only
 * be selected from this list.
 */
export function CalculationResults({
  results,
  project,
  onChangeProject,
  dates,
  years,
  pumpingSeries,
  series,
  calculationOptions,
  selection,
  onSelect,
  onLevelChange,
}: {
  results: SubBasinResult[];
  project: EvalHidProject;
  onChangeProject: (project: EvalHidProject) => void;
  dates: string[];
  years: number;
  /** The project's own loaded pumping series (state.loadedProject.series.pumping), untrimmed — already gated/trimmed to the calculated range inside, same as engine.ts's own pumping handling. `null` if none was ever loaded. */
  pumpingSeries: DailySeries | null;
  /** The project's full loaded series (precipitation/etp/temperature/pumping) — for `useSubBasinDetail` below, which needs to re-run ONE sub-basin's calculation on demand ("Ver detalle" no longer comes pre-attached to `results`, see engine.ts's own SubBasinResult.detail comment). */
  series: ProjectSeries;
  /** For `negativeAquifers`/`modelPumping` ("Negativos en acuíferos"/"Modelar bombeos") — `null` before the first successful calculation. */
  calculationOptions: CalculationOptions | null;
  selection: ResultSelection | null;
  onSelect: (selection: ResultSelection) => void;
  /** Reports the active level tab up to App.tsx (via usePanels.ts's `resultsLevel`) — while viewing "Outlet point"/"Zone", the map colors sub-basins by that field (see deriveMapModes.ts). This component still owns `level` itself below; this is only a mirror, not a controlled prop, so nothing here changes if the caller doesn't pass it. */
  onLevelChange?: (level: ResultLevel) => void;
}) {
  const t = useT();
  const [level, setLevel] = useState<ResultLevel>(selection?.level ?? "outletPoint");
  const [showDetail, setShowDetail] = useState(false);
  const [granularity, setGranularity] = useState<Granularity>("annual");

  useEffect(() => {
    onLevelChange?.(level);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [level]);

  // Clicking a sub-basin or outlet point on the map switches the active tab
  // to the matching one, even if another level was being viewed — so the
  // click always "shows" here.
  useEffect(() => {
    if (selection && selection.level !== level) setLevel(selection.level);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selection]);

  const coefficient = activeUnitsCoefficient(project);
  const unit = activeUnitName(project);
  const subBasins = project.subBasins;

  // No "Daily" option for a monthly-scale project (project.seriesScale) —
  // there's nothing finer than its own native step to show, and the engine
  // never produces daily-stepped output for one (see seriesScale.ts's own
  // header). `nativeGranularity(dates)` is kept as a defensive fallback for
  // any project that predates the seriesScale field somehow slipping past
  // storage.ts's own migration (GR2M was the one real case this used to be
  // the ONLY signal for). `effectiveGranularity` falls back to "annual" if
  // the project ever ends up monthly-native while "daily" was still
  // selected (e.g. the very first render, or switching to a differently-
  // native project).
  const availableGranularities: Granularity[] =
    project.seriesScale === "monthly" || nativeGranularity(dates) === "monthly"
      ? ["monthly", "annual"]
      : ["daily", "monthly", "annual"];
  const effectiveGranularity = availableGranularities.includes(granularity)
    ? granularity
    : "annual";
  const divisor = periodDivisor(effectiveGranularity, dates, years);

  // Memoized for the same reason the aggregates below are (see their own
  // comment): buildSubBasinRows does its own O(sub-basins × timesteps)
  // reduce() per row, so leaving it unmemoized re-ran that full scan on
  // every render of this component, not just ones that touch its inputs —
  // real gap found by deep-review (2026-08-29).
  const subBasinRows = useMemo(
    () => buildSubBasinRows(results, project, divisor, coefficient),
    [results, project, divisor, coefficient]
  );

  // Same gating/date-range convention as engine.ts's own pumping handling
  // (see useCalculation.ts's seriesForCalculation) — "Modelar bombeos"
  // unchecked means null regardless of whether a series was ever loaded,
  // and the range is narrowed to match `dates` (the already-calculated
  // range) exactly, so every array fed into aggregateContributionsByAquifer
  // lines up index-for-index with `results[i].contribution`. Falls back to
  // the untrimmed series (not null) if that exact range can't be found in
  // it — same fallback seriesForCalculation itself uses.
  const pumpingForAggregation = useMemo(() => {
    if (!calculationOptions?.modelPumping || !pumpingSeries || dates.length === 0) return null;
    const range = dateRangeIndices(pumpingSeries.dates, dates[0], dates[dates.length - 1]);
    return range ? trimSeriesByIndexRange(pumpingSeries, range) : pumpingSeries;
  }, [calculationOptions?.modelPumping, pumpingSeries, dates]);

  // Memoized: aggregateByKey (aggregation.ts) does a full O(sub-basins ×
  // timesteps) synchronous scan of `results` — real projects run tens of
  // thousands of daily rows (CLAUDE.md's Turia fixture: ~52 sub-basins ×
  // ~25,500 rows). Without memoizing, that scan re-ran on every render of
  // this component, including ones that don't touch its inputs at all (e.g.
  // switching the level/granularity tabs) — not just the units-selector
  // change it's meant to react to.
  const aquiferAggregates = useMemo(
    () =>
      aggregateContributionsByAquifer(
        results,
        subBasins,
        project.aquifers,
        coefficient,
        pumpingForAggregation,
        calculationOptions?.negativeAquifers ?? false
      ),
    [
      results,
      subBasins,
      project.aquifers,
      coefficient,
      pumpingForAggregation,
      calculationOptions?.negativeAquifers,
    ]
  );
  // Same reasoning as subBasinRows above — buildAquiferRows/buildAggregatedRows
  // below all do their own per-row reduce() over aggregated.volume/etc.
  const aquiferRows = useMemo(
    () => buildAquiferRows(aquiferAggregates, divisor),
    [aquiferAggregates, divisor]
  );
  const outletAggregates = useMemo(
    () =>
      aggregateContributionsByOutlet(
        results,
        subBasins,
        project.outletPoints,
        coefficient,
        aquiferAggregates
      ),
    [results, subBasins, project.outletPoints, coefficient, aquiferAggregates]
  );
  const outletRows = useMemo(
    () =>
      buildAggregatedRows(
        outletAggregates,
        (a) => a.outletCode,
        (a) => a.name,
        divisor
      ),
    [outletAggregates, divisor]
  );
  const zoneAggregates = useMemo(
    () => aggregateContributionsByZone(results, subBasins, project.zones, coefficient),
    [results, subBasins, project.zones, coefficient]
  );
  const zoneRows = useMemo(
    () =>
      buildAggregatedRows(
        zoneAggregates,
        (a) => a.zoneCode,
        (a) => a.name,
        divisor
      ),
    [zoneAggregates, divisor]
  );

  const levelRows =
    level === "subBasin"
      ? subBasinRows
      : level === "outletPoint"
        ? outletRows
        : level === "aquifer"
          ? aquiferRows
          : zoneRows;
  const effectiveId =
    selection?.level === level && levelRows.some((f) => f.id === selection.id)
      ? selection.id
      : (levelRows[0]?.id ?? null);

  const selectedResult =
    level === "subBasin" && effectiveId != null
      ? (results.find((r) => r.subBasinCode === effectiveId) ?? null)
      : null;
  // Only recomputed (see useSubBasinDetail's own comment — cheap, but no
  // reason to pay it while "Ver detalle" isn't even open) while `showDetail`
  // is true; `selectedResult.detail` itself is never populated any more
  // (dropped from the bulk calculation, see engine.ts) — `selectedResult`
  // still carries `aggregationDetail`, checked below only to decide whether
  // this sub-basin used a real model at all (same condition `.detail` used
  // to gate), not to read step-by-step values from it.
  const subBasinDetail = useSubBasinDetail(
    level === "subBasin" && showDetail ? effectiveId : null,
    project,
    series,
    calculationOptions
  );
  const selectedAggregate =
    level === "outletPoint" && effectiveId != null
      ? (outletAggregates.find((a) => a.outletCode === effectiveId) ?? null)
      : level === "zone" && effectiveId != null
        ? (zoneAggregates.find((a) => a.zoneCode === effectiveId) ?? null)
        : null;
  const selectedAquifer =
    level === "aquifer" && effectiveId != null
      ? (aquiferAggregates.find((a) => a.aquiferCode === effectiveId) ?? null)
      : null;
  const selectedName = levelRows.find((f) => f.id === effectiveId)?.name;

  const areaByCode = new Map(subBasins.map((c) => [c.subBasinCode, c.area]));

  return (
    <div>
      <p>
        {t("resultsPanel.calculatedCount", { n: subBasinRows.length, total: subBasins.length })}
        {subBasinRows.length < subBasins.length && (
          <>
            {" "}
            <InfoIcon text={t("resultsPanel.noColumnWarning")} />
          </>
        )}
      </p>

      <UnitsSelector project={project} onChangeProject={onChangeProject} />

      <div className="assigner-controls">
        <label>{t("resultsPanel.granularityTitle")}</label>
        <select
          value={effectiveGranularity}
          onChange={(e) => setGranularity(e.target.value as Granularity)}
        >
          {availableGranularities.map((g) => (
            <option key={g} value={g}>
              {t(GRANULARITY_LABEL_KEY[g])}
            </option>
          ))}
        </select>
        {(level === "outletPoint" || level === "zone" || level === "subBasin") && (
          <button
            className="button-icon"
            style={{ marginLeft: "auto" }}
            title={t("common.downloadCsv")}
            aria-label={t("common.downloadCsv")}
            onClick={() => {
              if (level === "outletPoint") {
                downloadText(
                  "Sal_Aportaciones.csv",
                  generateAggregatedContributionsCsv(dates, outletAggregates, effectiveGranularity)
                );
              } else if (level === "zone") {
                downloadText(
                  "Sal_Aportaciones_por_zona_web.csv",
                  generateAggregatedContributionsCsv(dates, zoneAggregates, effectiveGranularity)
                );
              } else {
                downloadText(
                  "Sal_Aportaciones_por_subcuenca_web.csv",
                  generateSubBasinContributionsCsv(dates, results, effectiveGranularity)
                );
              }
            }}
          >
            <DownloadIcon />
          </button>
        )}
      </div>

      <div className="edit-panel-tabs">
        {LEVELS.map((n) => (
          <button
            key={n.id}
            className={n.id === level ? "tab-active" : undefined}
            onClick={() => setLevel(n.id)}
          >
            {t(n.label)}
          </button>
        ))}
        {level !== "zone" && (
          <InfoIcon
            text={t("resultsPanel.alsoCanSelect", {
              what: t(
                level === "subBasin"
                  ? "resultsPanel.oneSubBasin"
                  : level === "aquifer"
                    ? "resultsPanel.oneAquifer"
                    : "resultsPanel.oneOutletPoint"
              ),
            })}
          />
        )}
      </div>

      {selectedResult && (
        <ContributionChart
          result={selectedResult}
          dates={dates}
          area={areaByCode.get(selectedResult.subBasinCode) ?? 0}
          coefficient={coefficient}
          unit={unit}
          granularity={effectiveGranularity}
        />
      )}
      {selectedAggregate && selectedName && (
        <AggregatedContributionChart
          title={selectedName}
          volume={selectedAggregate.volume}
          dates={dates}
          unit={unit}
          granularity={effectiveGranularity}
        />
      )}
      {selectedAquifer && selectedName && (
        <AggregatedContributionChart
          title={selectedName}
          volume={selectedAquifer.discharge}
          dates={dates}
          unit={unit}
          granularity={effectiveGranularity}
        />
      )}

      {level === "outletPoint" && (
        <AggregatedTable
          columnLabel={t("legend.outletPoint")}
          unit={unit}
          granularity={effectiveGranularity}
          rows={outletRows}
          selectedId={effectiveId}
          onSelectRow={(id) => onSelect({ level: "outletPoint", id })}
        />
      )}

      {level === "zone" && (
        <AggregatedTable
          columnLabel={t("resultsPanel.zone")}
          unit={unit}
          granularity={effectiveGranularity}
          rows={zoneRows}
          selectedId={effectiveId}
          onSelectRow={(id) => onSelect({ level: "zone", id })}
        />
      )}

      {level === "aquifer" && (
        <AquiferTable
          unit={unit}
          granularity={effectiveGranularity}
          rows={aquiferRows}
          selectedId={effectiveId}
          onSelectRow={(id) => onSelect({ level: "aquifer", id })}
        />
      )}

      {level === "subBasin" && (
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>{t("resultsPanel.subBasin")}</th>
                <th>{t("resultsPanel.areaKm2")}</th>
                <th>
                  {t(
                    project.seriesScale === "monthly"
                      ? "resultsPanel.mmPerMonth"
                      : "resultsPanel.mmPerDay"
                  )}
                </th>
                <th>
                  {t("resultsPanel.unitPerPeriod", {
                    unit,
                    period: t(GRANULARITY_PERIOD_KEY[effectiveGranularity]),
                  })}
                </th>
              </tr>
            </thead>
            <tbody>
              {subBasinRows.map((f) => (
                <tr
                  key={f.id}
                  className={f.id === effectiveId ? "row-selected" : undefined}
                  onClick={() => onSelect({ level: "subBasin", id: f.id })}
                >
                  <td>{f.name}</td>
                  <td>{f.area.toFixed(1)}</td>
                  <td>{f.averagePerStepMm.toFixed(3)}</td>
                  <td>{f.hm3PerPeriod.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selectedResult?.aggregationDetail && (
        <>
          <button
            className="button"
            style={{ marginTop: "1rem" }}
            onClick={() => setShowDetail((v) => !v)}
          >
            {showDetail ? t("resultsPanel.hideDetail") : t("resultsPanel.viewDetail")}
          </button>
          {showDetail && subBasinDetail?.detail && (
            <DetailChart result={subBasinDetail} dates={dates} scale={project.seriesScale} />
          )}
        </>
      )}
    </div>
  );
}
